import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, SlidersHorizontal } from "lucide-react";
import EntityConfigDialog from "@/components/admin/EntityConfigDialog";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const CURRENCY_LABELS = { BS: "Bolívares (Bs)", USD: "Dólares (USD)", COP: "Pesos (COP)" };
const ALL_CURRENCIES = ["BS", "USD", "COP"];
const toNum = (v) => (v === "" || v == null ? 0 : Number(v));
const EMPTY = { name: "", code: "", city: "", commission_pct: 10, currencies: ["BS", "USD"], active: true };

export default function BancasPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [configFor, setConfigFor] = useState(null);

  const submitSave = () => {
    if (!form.name?.trim() || !form.code?.trim()) { toast.error("Nombre y código son obligatorios"); return; }
    save.mutate({ ...form, name: form.name.trim(), code: form.code.trim() });
  };

  const { data: bancas = [], isLoading } = useQuery({
    queryKey: ["bancas"],
    queryFn: async () => (await api.get("/bancas")).data,
  });

  const save = useMutation({
    mutationFn: async (raw) => {
      const payload = { ...raw, commission_pct: toNum(raw.commission_pct) };
      if (editing) return (await api.patch(`/bancas/${editing.id}`, payload)).data;
      return (await api.post("/bancas", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Banca actualizada" : "Banca creada");
      qc.invalidateQueries({ queryKey: ["bancas"] });
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/bancas/${id}`)).data,
    onSuccess: () => {
      toast.success("Banca eliminada");
      qc.invalidateQueries({ queryKey: ["bancas"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al eliminar"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ name: b.name, code: b.code, city: b.city || "", commission_pct: b.commission_pct, currencies: b.currencies?.length ? b.currencies : ["BS", "USD"], active: b.active });
    setOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Bancas / Agencias</h1>
          <p className="text-sm text-muted-foreground">Nivel superior de la jerarquía operativa.</p>
        </div>
        <Button onClick={openCreate} data-testid="banca-create-button"><Plus size={16} className="mr-1.5"/>Nueva Banca</Button>
      </div>

      <Card className="bg-card border-border overflow-hidden" data-testid="admin-table-bancas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Ciudad</th>
                <th className="px-4 py-3 text-right">Comisión %</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && bancas.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sin bancas registradas.</td></tr>}
              {bancas.map((b) => (
                <tr key={b.id} className="border-t border-border/60 hover:bg-secondary/50">
                  <td className="px-4 py-3 font-mono text-sky-300">{b.code}</td>
                  <td className="px-4 py-3 font-medium">{b.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{b.city || "-"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{b.commission_pct}%</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${b.active ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" : "border-rose-500/30 text-rose-300 bg-rose-500/10"}`}>
                      {b.active ? "Activa" : "Inactiva"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setConfigFor(b)} title="Cupos y comisiones" data-testid={`banca-config-${b.code}`}><SlidersHorizontal size={16}/></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`banca-edit-${b.code}`}><Pencil size={16}/></Button>
                      <Button variant="ghost" size="icon" className="text-rose-300" onClick={() => { if (window.confirm(`Eliminar ${b.name}?`)) del.mutate(b.id); }} data-testid={`banca-delete-${b.code}`}><Trash2 size={16}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar banca" : "Nueva banca"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="banca-form-name"/>
            </div>
            <div className="space-y-2">
              <Label>Código</Label>
              <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} data-testid="banca-form-code"/>
            </div>
            <div className="space-y-2">
              <Label>Ciudad</Label>
              <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Comisión %</Label>
              <Input type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({...form, commission_pct: e.target.value})} onWheel={(e) => e.currentTarget.blur()} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Monedas habilitadas <span className="text-[11px] font-normal text-muted-foreground">(definen las monedas disponibles para grupos y agencias)</span></Label>
              <div className="flex gap-2" data-testid="banca-form-currencies">
                {ALL_CURRENCIES.map((c) => {
                  const on = form.currencies.includes(c);
                  return (
                    <button key={c} type="button" data-testid={`banca-form-currency-${c}`}
                      onClick={() => {
                        const next = on ? form.currencies.filter((x) => x !== c) : [...form.currencies, c];
                        if (next.length === 0) { toast.error("Selecciona al menos una moneda"); return; }
                        setForm({ ...form, currencies: next });
                      }}
                      className={`rounded-lg border-2 px-4 py-2 text-sm font-bold transition-colors ${on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                      {CURRENCY_LABELS[c] || c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} id="active" />
              <Label htmlFor="active">Activa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submitSave} disabled={save.isPending} data-testid="banca-form-submit">{editing ? "Guardar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {configFor && <EntityConfigDialog open={!!configFor} onClose={() => setConfigFor(null)} entityType="banca" entity={configFor}/>}
    </div>
  );
}
